import { useNavigate } from 'react-router-dom'
import {
  CheckCircle2,
  Clock3,
  CreditCard,
  MessageCircle,
  Package,
  PackageCheck,
} from 'lucide-react'
import { useUsersApi } from 'src/api/db/apiUsers'
import { IMAGE_URLS } from 'src/assets/urlLinks'
import { ActionCard, type ActionTone } from 'src/pages/_admin/Dashboard/components/ActionCard'
import { DashboardSection } from 'src/pages/_admin/Dashboard/components/DashboardSection'
import { buildAppRequestPath, myAccountRequestsPath } from 'src/routes/paths'
import { PageContainer } from 'src/shared/components/PageContainer'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { FullScreenLoader } from 'src/shared/components/FullScreenLoader'
import { Image } from 'src/shared/components/ui/image'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import {
  OnboardingAnchor,
  ONBOARDING_ELEMENT_IDS,
  ONBOARDING_STORAGE_KEYS,
  useOnboarding,
} from 'src/shared/hooks/useOnboarding'
import { capitalize } from 'src/shared/utils'
import { buildRequestsListSearch } from '../Requests/requestNavParams'
import { DashboardBackdrop } from './components/DashboardBackdrop'
import { DashboardOrderListCard } from './components/DashboardOrderListCard'
import { DashboardSearchBar } from './components/DashboardSearchBar'
import { DASHBOARD_ACTION_LIMIT } from './consts'
import {
  useDashboardOrders,
  useDashboardWaiting,
  type CustomerAttentionReason,
} from './hooks'

const ATTENTION_LABELS: Record<
  CustomerAttentionReason,
  { en: string; sv: string; icon: typeof MessageCircle; tone: ActionTone }
> = {
  approve_quote_and_proofs: {
    en: 'Approve quote and proofs',
    sv: 'Godkänn offert och korrekturer',
    icon: CheckCircle2,
    tone: 'blue',
  },
  confirm_order: {
    en: 'Confirm order',
    sv: 'Bekräfta order',
    icon: CreditCard,
    tone: 'teal',
  },
}

export const DashboardPage = () => {
  const { t } = useLocalization()
  const navigate = useNavigate()
  const { user } = useUsersApi()
  const { allQuotes, currentOrders, previousOrders, isLoadingQuotes } =
    useDashboardOrders()
  const { chatsWaiting, ordersWaiting, isLoadingWaiting } =
    useDashboardWaiting(allQuotes)
  const { activeStep, acknowledge } = useOnboarding({
    storageKey: ONBOARDING_STORAGE_KEYS.dashboard,
    steps: [
      {
        elementId: ONBOARDING_ELEMENT_IDS.dashboardPrompt,
        content: t(
          'Type what you want here. This starts a new request.',
          'Skriv vad du vill ha här. Det startar en ny förfrågan.'
        ),
      },
    ],
  })

  if (isLoadingQuotes || isLoadingWaiting) {
    return <FullScreenLoader />
  }

  const firstName = user?.data?.first_name
    ? capitalize(user.data.first_name)
    : null
  const headline = firstName
    ? t(`Hello, ${firstName}`, `Hej, ${firstName}`)
    : t('Hello', 'Hej')

  const chatItems = chatsWaiting.slice(0, DASHBOARD_ACTION_LIMIT)
  const orderItems = ordersWaiting.slice(0, DASHBOARD_ACTION_LIMIT)
  const chatTotal = chatsWaiting.length
  const orderTotal = ordersWaiting.length

  const queueLine =
    chatTotal > 0 && orderTotal > 0
      ? t(
          `${chatTotal} chats and ${orderTotal} orders waiting on you`,
          `${chatTotal} chattar och ${orderTotal} ordrar väntar på dig`
        )
      : chatTotal > 0
        ? t(
            `${chatTotal} chats waiting on you`,
            `${chatTotal} chattar väntar på dig`
          )
        : orderTotal > 0
          ? t(
              `${orderTotal} orders waiting on you`,
              `${orderTotal} ordrar väntar på dig`
            )
          : null

  const activeListSearch = buildRequestsListSearch({ segment: 'active' })
  const deliveredListSearch = buildRequestsListSearch({ segment: 'delivered' })

  return (
    <Column className="relative min-h-full">
      <DashboardBackdrop />

      <PageContainer width="md" className="relative gap-8 pt-16">
        <Column className="items-center gap-2 text-center">
          <Image
            src={IMAGE_URLS.logos.klayder}
            alt="Klayder"
            className="h-8 w-auto"
          />
          <Column className="gap-1">
            <h1>{headline}</h1>
            {queueLine ? (
              <p className="text-sm text-muted-foreground">{queueLine}</p>
            ) : null}
          </Column>
        </Column>

        <DashboardSearchBar />

        <Column className="gap-6">
          <DashboardSection
            title={t('Chats waiting for you', 'Chattar som väntar på dig')}
            description={t(
              'Team messages that need your reply.',
              'Teammeddelanden som behöver ditt svar.'
            )}
            count={chatTotal}
            icon={MessageCircle}
            accent="violet"
            viewAllHref={myAccountRequestsPath}
            viewAllLabel={t('View all requests', 'Visa alla förfrågningar')}
          >
            {chatItems.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t(
                  'No chats waiting for a reply.',
                  'Inga chattar väntar på svar.'
                )}
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {chatItems.map((quote) => (
                  <ActionCard
                    key={quote.id}
                    icon={MessageCircle}
                    tone="violet"
                    quote={quote}
                    title={
                      quote.name ||
                      t('Untitled request', 'Namnlös förfrågan')
                    }
                    badge={t('Needs reply', 'Behöver svar')}
                    onClick={() =>
                      navigate(buildAppRequestPath(quote.public_id ?? ''))
                    }
                  />
                ))}
              </div>
            )}
          </DashboardSection>

          <DashboardSection
            title={t('Orders waiting on you', 'Ordrar som väntar på dig')}
            description={t(
              'Checkout steps that need your approval.',
              'Kassasteg som behöver ditt godkännande.'
            )}
            count={orderTotal}
            icon={Package}
            accent="blue"
            viewAllHref={myAccountRequestsPath}
            viewAllLabel={t('View all requests', 'Visa alla förfrågningar')}
          >
            {orderItems.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t(
                  'No orders waiting on you right now.',
                  'Inga ordrar väntar på dig just nu.'
                )}
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {orderItems.map(({ quote, reason }) => {
                  const meta = ATTENTION_LABELS[reason]
                  return (
                    <ActionCard
                      key={quote.id}
                      icon={meta.icon}
                      tone={meta.tone}
                      quote={quote}
                      title={
                        quote.name ||
                        t('Untitled request', 'Namnlös förfrågan')
                      }
                      badge={t(meta.en, meta.sv)}
                      onClick={() =>
                        navigate(
                          buildAppRequestPath(quote.public_id ?? '', 'checkout')
                        )
                      }
                    />
                  )
                })}
              </div>
            )}
          </DashboardSection>
        </Column>

        <Row className="gap-4">
          <DashboardOrderListCard
            title={t('Current orders', 'Aktuella ordrar')}
            description={t(
              'Track active orders and ongoing production.',
              'Följ aktiva ordrar och pågående produktion.'
            )}
            icon={Clock3}
            orders={currentOrders}
            listSearch={activeListSearch}
            viewAllSearch={activeListSearch}
            variant="current"
            emptyMessage={t(
              'No active orders yet.',
              'Inga aktiva ordrar ännu.'
            )}
          />

          <DashboardOrderListCard
            title={t('Previous orders', 'Tidigare ordrar')}
            description={t(
              'Browse delivered orders and past orders.',
              'Bläddra bland levererade och tidigare ordrar.'
            )}
            icon={PackageCheck}
            orders={previousOrders}
            listSearch={deliveredListSearch}
            viewAllSearch={deliveredListSearch}
            variant="previous"
            emptyMessage={t(
              'No previous orders yet.',
              'Inga tidigare ordrar ännu.'
            )}
          />
        </Row>
        <div className="h-16 shrink-0" aria-hidden />
      </PageContainer>

      <OnboardingAnchor activeStep={activeStep} onAcknowledge={acknowledge} />
    </Column>
  )
}
